import { useCallback, useEffect, useMemo, useRef } from 'react';

import useSideDetail from '#/hooks/useSideDetail';
import ListTablePanel from '#/modules/commonComponents/table/ListTablePanel';
import { buildFilterStatusFieldsFromColumns } from '#/modules/commonComponents/table/buildFilterStatusFields';
import { DEFAULT_LIST_SORT_FIELD_LABELS } from '#/modules/commonComponents/table/listFilterKeys';
import AftersalesOpsPageHeader from '#/modules/crmMgmt/aftersales/components/AftersalesOpsPageHeader';
import RepairReportSideMsg from '#/modules/crmMgmt/aftersales/repairReport/components/RepairReportSideMsg';
import { buildRepairReportColumns } from '#/modules/crmMgmt/aftersales/repairReport/data/repairReportColumns';
import {
  getRepairReportApiErrorMessage,
} from '#/modules/crmMgmt/aftersales/repairReport/helper/apiError';
import { applyRepairDateRangeToFilters } from '#/modules/crmMgmt/aftersales/repairReport/helper/repairDateListFilter';
import useRepairReportList from '#/modules/crmMgmt/aftersales/repairReport/hooks/useRepairReportList';
import PageShell from '#/modules/infra/layout/PageShell';
import SideMsgLayout from '#/modules/infra/layout/SideMsgLayout';

const SORT_FIELD_LABELS = {
  ...DEFAULT_LIST_SORT_FIELD_LABELS,
  repairReportNo: '報告單號',
  repairDate: '維修日',
  status: '狀態',
  serialNo: '產品序號',
  itemName: '品名',
  createdAt: '建立時間',
};

const RepairReportListPage = () => {
  const listRef = useRef(null);
  const { displayData, setSelectedData } = useSideDetail();

  const {
    rows,
    pagination,
    isLoading,
    isError,
    error,
    refetch,
    listQuery,
    setPage,
    setPageSize,
    setKeyword,
    setFilter,
    setSort,
    removeSortAt,
    resetListQuery,
    setListQuery,
  } = useRepairReportList();

  const setRepairDateRangeFilter = useCallback((range) => {
    setListQuery((prev) => ({
      ...prev,
      page: 1,
      filters: applyRepairDateRangeToFilters(prev.filters, range),
    }));
  }, [setListQuery]);

  const handleFilterChange = useCallback((key, value) => {
    if (key === 'repairDateFrom' && value === undefined) {
      setRepairDateRangeFilter(undefined);
      return;
    }
    setFilter(key, value);
  }, [setFilter, setRepairDateRangeFilter]);

  const columns = useMemo(
    () => buildRepairReportColumns({
      filters: listQuery.filters || {},
      onFilterChange: handleFilterChange,
      onRepairDateRangeChange: setRepairDateRangeFilter,
    }),
    [listQuery.filters, handleFilterChange, setRepairDateRangeFilter],
  );

  const filterStatusFields = useMemo(
    () => buildFilterStatusFieldsFromColumns(columns, { filters: listQuery.filters }),
    [columns, listQuery.filters],
  );

  useEffect(() => {
    if (!displayData?.id) {
      return;
    }
    const fresh = rows.find((row) => row.id === displayData.id);
    if (!fresh) {
      return;
    }
    const sameStatus = fresh.status === displayData.status;
    const sameUpdatedAt = fresh.record?.updatedAt === displayData.record?.updatedAt;
    if (sameStatus && sameUpdatedAt) {
      return;
    }
    setSelectedData(fresh);
  }, [rows, displayData, setSelectedData]);

  const handleSortingChange = useCallback((sorting) => {
    setSort((sorting ?? []).map((item) => ({
      id: item.id,
      desc: Boolean(item.desc),
    })));
  }, [setSort]);

  const handleRowClick = useCallback((row, isSelected) => {
    if (isSelected) {
      setSelectedData(row.original);
      return;
    }
    setSelectedData(null);
  }, [setSelectedData]);

  return (
    <PageShell variant="listWithSide">
      <div className="layout-shell__body flex min-h-0 flex-col">
        <AftersalesOpsPageHeader page="repairReport" variant="list" />
        <SideMsgLayout>
          <div className="table-panel min-h-0 xl:h-full">
            <ListTablePanel
              ref={listRef}
              columns={columns}
              rows={rows}
              pagination={pagination}
              isLoading={isLoading}
              isError={isError}
              errorMessage={
                isError
                  ? getRepairReportApiErrorMessage(error, '載入列表失敗（API 可能尚未部署）')
                  : undefined
              }
              onRetry={isError ? () => { refetch(); } : undefined}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
              onRowClick={handleRowClick}
              onSearch={setKeyword}
              onReset={resetListQuery}
              initialKeyword={listQuery.keyword}
              searchPlaceholder="搜尋報告單號或備註"
              listQuery={listQuery}
              filterStatusFields={filterStatusFields}
              sortFieldLabels={SORT_FIELD_LABELS}
              onFilterChange={handleFilterChange}
              onKeywordClear={() => setKeyword('')}
              onSortRemove={removeSortAt}
              onSortingChange={handleSortingChange}
            />
          </div>
          <RepairReportSideMsg displayData={displayData} onRefetch={refetch} />
        </SideMsgLayout>
      </div>
    </PageShell>
  );
};

export default RepairReportListPage;
